
import { FileText, Search, GitBranch, LineChart, MessageSquare } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../Button";

const LitigationContent = () => {
  return (
    <div className="container mx-auto px-4 py-16">
      {/* Hero Section */}
      <div className="mb-16 text-center">
        <h1 className="text-4xl md:text-5xl font-bold mb-6">Litigation, E-Discovery & Research</h1>
        <p className="text-xl md:text-2xl text-white/70 max-w-3xl mx-auto">
          AI-powered litigation assistants provide transformative features that streamline the identification, review, and analysis of electronically stored information.
        </p>
      </div>

      {/* Overview Section */}
      <div className="mb-20">
        <div className="prose prose-lg prose-invert max-w-4xl mx-auto">
          <p>
            AI-powered litigation assistants including eDiscovery, Research and Predictive Analytics applications for legal teams provide transformative features and benefits that streamline the identification, review, and analysis of electronically stored information (ESI). The benefits include faster document processing, improved accuracy in identifying relevant information, reduced costs, and enhanced strategy and decision-making capabilities. These tools empower legal teams to handle complex cases efficiently while also increasing odds of favorable outcomes.
          </p>
        </div>
      </div>

      {/* Key Features Section */}
      <div className="mb-20">
        <h2 className="text-3xl font-bold mb-12 text-center">Key Features</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <div className="mb-4 text-blue-400">
              <FileText size={36} />
            </div>
            <h3 className="text-xl font-bold mb-3">Automated Document Review</h3>
            <p className="text-white/70">
              AI accelerates the review process by identifying relevant documents, detecting privileged information, and flagging sensitive data like Personally Identifiable Information (PII) with precision.
            </p>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <div className="mb-4 text-purple-400">
              <Search size={36} />
            </div>
            <h3 className="text-xl font-bold mb-3">Advanced Search and Filtering</h3>
            <p className="text-white/70">
              Tools utilize natural language processing (NLP), concept searches, and entity filtering to quickly locate critical information across large datasets.
            </p>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <div className="mb-4 text-green-400">
              <GitBranch size={36} />
            </div>
            <h3 className="text-xl font-bold mb-3">Multimedia and Multilingual Support</h3>
            <p className="text-white/70">
              AI handles diverse data formats, including audio, video, and foreign language documents, ensuring comprehensive coverage.
            </p>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <div className="mb-4 text-amber-400">
              <LineChart size={36} />
            </div>
            <h3 className="text-xl font-bold mb-3">Predictive Analytics</h3>
            <p className="text-white/70">
              Machine learning algorithms identify patterns, prioritize key evidence, and provide insights to guide case strategy development.
            </p>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <div className="mb-4 text-red-400">
              <MessageSquare size={36} />
            </div>
            <h3 className="text-xl font-bold mb-3">Enhanced Security</h3>
            <p className="text-white/70">
              Features like encryption, audit trails, and real-time threat detection ensure compliance with legal standards while protecting sensitive data.
            </p>
          </div>
        </div>
      </div>

      {/* Case Studies Section */}
      <div className="mb-20">
        <h2 className="text-3xl font-bold mb-12 text-center">Curated Case Studies</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">Legal Aid</h3>
            <p className="text-white/70 mb-4">
              GenAI levels the legal playing field for abuse survivors
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://legal.thomsonreuters.com/en/insights/case-studies/genai-levels-the-legal-playing-field-for-abuse-survivors', '_blank')}
            >
              Read Study
            </Button>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">Rapid Investigations</h3>
            <p className="text-white/70 mb-4">
              How J2 Risk Advisors conducts investigations in just 15 minutes
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://legal.thomsonreuters.com/en/insights/case-studies/how-j2-risk-advisors-conducts-investigations-in-15-minutes', '_blank')}
            >
              Read Study
            </Button>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">Environmental Justice</h3>
            <p className="text-white/70 mb-4">
              How an Environmental Justice Nonprofit Bolsters Their Litigation Strategy with Ediscovery Tech
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://www.everlaw.com/blog/case-studies/environmental-justice-nonprofit-ediscovery-tech/', '_blank')}
            >
              Read Study
            </Button>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">Technical Challenges</h3>
            <p className="text-white/70 mb-4">
              Overcoming the Technical Ediscovery Challenges in Multidistrict Litigation
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://www.everlaw.com/blog/everlaw/challenges-multidistrict-litigation/', '_blank')}
            >
              Read Study
            </Button>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">Cost Control</h3>
            <p className="text-white/70 mb-4">
              HP Tackles Unpredictable Litigation Costs with User-Friendly Technology
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://www.everlaw.com/blog/case-studies/hp-tackles-unpredictable-litigation-costs-with-user-friendly-technology/', '_blank')}
            >
              Read Study
            </Button>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">Legal Research</h3>
            <p className="text-white/70 mb-4">
              How NexLaw AI Makes Legal Research and Argumentation Easier for Nick Tan & Co.
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://www.nexlaw.ai/how-nexlaw-ai-makes-legal-research-and-argumentation-easier-for-nick-tan-co/', '_blank')}
            >
              Read Study
            </Button>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">Outcome Prediction</h3>
            <p className="text-white/70 mb-4">
              AI-Powered Legal Case Outcome Prediction Methods
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://www.pre-dicta.com/ai-powered-legal-case-outcome-prediction-methods/', '_blank')}
            >
              Read Study
            </Button>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">Advanced Analytics</h3>
            <p className="text-white/70 mb-4">
              Plaintiffs Wins Motion by Using Advanced Analytics
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://downloads.casepoint.com/hubfs/Casepoint%20Case%20Studies/Casepoint-Case-Study-Plaintiffs-Firm-Win-Motion-Using-Advanced-Analytics.pdf?_gl=1mmh2l2_gcl_au*MTc5NzgzMzMwLjE3NDM1MzY3Njg.', '_blank')}
            >
              Read Study
            </Button>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">Business Improvement</h3>
            <p className="text-white/70 mb-4">
              Lex Machina helps Law Firms Win More Business and Win More Cases
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://lexmachina.com', '_blank')}
            >
              Read Study
            </Button>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">Judicial Predictions</h3>
            <p className="text-white/70 mb-4">
              AI Tells Lawyers How Judges Are Likely To Rule
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://www.axios.com/2023/09/12/ai-judges-trials-predictions', '_blank')}
            >
              Read Study
            </Button>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="text-center">
        <h2 className="text-3xl font-bold mb-6">Elevate Your Litigation Strategy</h2>
        <p className="text-xl text-white/70 mb-8 max-w-2xl mx-auto">
          Discover how our AI-powered litigation and e-discovery solutions can transform your case outcomes.
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/contact">
            <Button variant="glass" size="lg" hasAnimation={true}>Contact Us</Button>
          </Link>
          <Link to="/services/strategy">
            <Button variant="outline" size="lg">Explore Strategy Services</Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LitigationContent;
